import { useRef } from 'react';
import { useState } from 'react';
import { useContext } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { uiActions } from '../../store/ui-slice';
import AuthContext from '../../store/auth-context';
import {
	editStudyInformationForm,
	getFormOptionslist,
	getStudyById
} from '../../api/studiesAPI';
import Loader from '../ui/Loader';
import Button from '../ui/Button';
import { Icon } from '@mui/material';
import './StudyInformationForm.scss';

const StudyQCTForm = ({ studyId, onSendQCT }) => {
	const dispatch = useDispatch();
	const authCtx = useContext(AuthContext);
	const queryClient = useQueryClient();

	const q1Ref = useRef('');
	const q2Ref = useRef('');
	const q3Ref = useRef('');
	const q4Ref = useRef('');
	const q5Ref = useRef('');

	const [editForm, setEditForm] = useState(true);
	const [showButtons, setShowButtons] = useState(true);
	const [showStudyDetails, setShowStudyDetails] =
		useState(false);
	const [showInvDrugOrDev, setShowInvDrugOrDev] =
		useState(false);
	const [showQCTA, setShowQCTA] = useState(true);

	const refetchStudyRef = useRef(true);

	const { isLoading: isLoading1, data: studyData } =
		useQuery({
			queryKey: ['study', studyId],
			queryFn: () => getStudyById(studyId),
			retry: false,
			staleTime: 3000000, //5 minutes to refetch
			refetchInterval: 20000,
			enabled: refetchStudyRef.current
		});

	const { isLoading: isLoading2, data } = useQuery({
		queryKey: ['form-options'],
		queryFn: getFormOptionslist,
		retry: false,
		staleTime: 3000000 //5 minutes to refetch
	});

	if (isLoading2 || isLoading1 || !studyData) {
		return <Loader />;
	}

	const { study } = studyData.data;

	if (!study.processed) {
		return (
			<>
				<span>Loading…</span>
				<Loader></Loader>
			</>
		);
	}
	refetchStudyRef.current = false;

	const onEditForm = () => {
		setEditForm(false);
	};

	const onCancelEditForm = () => {
		setEditForm(true);
	};

	const q1Response =
		study.questions.one.answer === 'Yes' ? 'Yes' : 'No';

	const q2Response =
		study.questions.two.answer === 'True' ||
		study.questions.two.answer === 'Yes'
			? 'Yes'
			: 'No';

	const q3Response =
		study.questions.three.answer === 'Yes' ? 'Yes' : 'No';

	const q4Response =
		study.questions.four.answer === 'Yes' ? 'Yes' : 'No';

	const q5Response =
		study.questions.five.answer === 'Yes' ? 'Yes' : 'No';

	const onSendStudyInfo = (event) => {
		event.preventDefault();

		const questions = {
			one: q1Ref.current ? q1Ref.current.value : q1Response,
			two: q2Ref.current ? q2Ref.current.value : q2Response,
			three: q3Ref.current
				? q3Ref.current.value
				: q3Response,
			four: q4Ref.current
				? q4Ref.current.value
				: q4Response,
			five: q5Ref.current ? q5Ref.current.value : q5Response
		};

		editStudyInformationForm({
			id: studyId,
			changes: {},
			questions
		})
			.then((result) => {
				console.log(result);
				dispatch(
					uiActions.launchNotification({
						title:
							'The Qualifying Clinical Trial (QTC) Analysis has been accepted successfully.',
						message:
							'You study request has been created and is under review',
						status: 'success'
					})
				);
				setShowButtons(false);
				setEditForm(false);
				queryClient.invalidateQueries('study');
				if (onSendQCT) {
					onSendQCT();
				}
			})
			.catch(() => {
				dispatch(
					uiActions.launchNotification({
						title: `The form can not be accepted`,
						message:
							'An unexpected error has ocurred, please try again!',
						status: 'error'
					})
				);
			});
	};

	return (
		<>
			<div className='study-information-container-title'>
				<h5>Qualifying Clinical Trial (QCT) Analysis</h5>
				<p className='study-information-container-subtitle'>
					Below please find a summary of the study
					information and the QCT Analysis.
				</p>
			</div>
			<div className='study-information-container'>
				<form className='study-information-first-form'>
					<div
						className='study-information-title with-botton-border'
						onClick={() =>
							setShowStudyDetails((prev) => !prev)
						}
					>
						<span className='study-information-title-label'>
							Study Information
						</span>
						{!showStudyDetails && (
							<Icon className='expand-control'>
								expand_more
							</Icon>
						)}
						{showStudyDetails && (
							<Icon className='expand-control'>
								expand_less
							</Icon>
						)}
					</div>
					{showStudyDetails && (
						<div className='form-container'>
							<div className='control-container row study-info-row-one'>
								<div className='col-sm-6'>
									<label>Deparment</label>
									<input
										id='deparment'
										type='text'
										defaultValue={study.department}
										className='form-control'
										disabled
									/>
								</div>
								<div className='col-sm-6'>
									<label>P.I name</label>
									<select
										name='protocol'
										id='pi-names'
										placeholder='select a name'
										className='form-control'
										disabled
									>
										<option>
											{study.pi?.firstname}{' '}
											{study.pi?.lastname}
										</option>
									</select>
								</div>
							</div>
							<div className='control-container row study-info-row'>
								<div className='col-sm-4'>
									<label>Protocol Title</label>
									<input
										type='text'
										defaultValue={study.title}
										className='form-control'
										disabled
									/>
								</div>
								<div className='col-sm-4'>
									<label>Protocol version</label>
									<input
										type='text'
										placeholder={study.version}
										className='form-control'
										disabled
									/>
								</div>
								<div className='col-sm-4'>
									<label>Protocol Date</label>
									<input
										type='date'
										defaultValue={
											study.protocolDate
												? study.protocolDate.split('T')[0]
												: ''
										}
										className='form-control'
										disabled
									/>
								</div>
							</div>
							<div className='control-container row study-info-row'>
								<div className='col-sm-4'>
									<label>
										Informed Consent Form Version
									</label>
									<input
										type='text'
										placeholder='1.0'
										className='form-control'
										disabled
									/>
								</div>
								<div className='col-sm-4'>
									<label>Informed Consent Form Date</label>
									<input
										type='date'
										defaultValue={
											study.informedConsentFormDate
												? study.informedConsentFormDate.split(
														'T'
													)[0]
												: ' '
										}
										className='form-control'
										disabled
									/>
								</div>
								<div className='col-sm-4'>
									<label>IRB Approval Date</label>
									<input
										type='date'
										defaultValue={
											study.IRBApprovalDate
												? study.IRBApprovalDate.split(
														'T'
													)[0]
												: ''
										}
										className='form-control'
										disabled
									/>
								</div>
							</div>
							<div className='control-container row study-info-row'>
								<div className='col-sm-6'>
									<label>Funding Sponsor</label>
									<input
										type='text'
										defaultValue={study.sponsor}
										className='form-control'
										disabled
									/>
								</div>
								<div className='col-sm-6'>
									<label>Funding Type</label>
									<select
										name='funding'
										id='funding-type'
										disabled
										defaultValue={study.fundingType}
										className='form-control'
									>
										<option>{study.fundingType}</option>
									</select>
								</div>
							</div>
							<div className='control-container row study-info-row'>
								<div className='col-md-3'>
									<label>
										Clinical Trial Agreement Version
									</label>
									<input
										type='text'
										defaultValue={
											study.clinicalTrialAgreementVersion
										}
										className='form-control'
										disabled
									/>
								</div>
								<div className='col-md-4'>
									<label>
										Clinical Trial Agreement Date
									</label>
									<input
										type='date'
										defaultValue={
											study.clinicalTrialAgreementDate
												? study.clinicalTrialAgreementDate.split(
														'T'
													)[0]
												: ' '
										}
										className='form-control'
										disabled
									/>
								</div>
								<div className='col-md-5'>
									<label>
										Clinical Trial Agreement Execution Date
									</label>
									<input
										type='date'
										defaultValue={
											study.clinicalTrialAgreementExecutionDate
												? study.clinicalTrialAgreementExecutionDate.split(
														'T'
													)[0]
												: ''
										}
										className='form-control'
										disabled
									/>
								</div>
							</div>
						</div>
					)}
				</form>
				<form>
					<div
						className='study-information-title with-top-botton-borders'
						onClick={() =>
							setShowInvDrugOrDev((prev) => !prev)
						}
					>
						<span className='study-information-title-label'>
							Investigational Drug and Device Information
						</span>

						{!showInvDrugOrDev && (
							<Icon className='expand-control'>
								expand_more
							</Icon>
						)}
						{showInvDrugOrDev && (
							<Icon className='expand-control'>
								expand_less
							</Icon>
						)}
					</div>
					{showInvDrugOrDev && (
						<div className='form-container'>
							<div className='control-container row study-info-row'>
								<div className='col-sm-6'>
									<label>
										Name of Investigational Drug
									</label>
									<input
										type='text'
										defaultValue={
											study.questions
												.nameOfInvestigationalDrug
										}
										className='form-control'
										disabled
									/>
								</div>
								<div className='col-sm-6'>
									<label>IND#</label>
									<input
										type='text'
										defaultValue={study.questions.IDN}
										className='form-control'
										disabled
									/>
								</div>
							</div>
							<div className='control-container row study-info-row'>
								<div className='col-sm-6'>
									<label>
										Name of Investigational Device
									</label>
									<input
										type='text'
										defaultValue={
											study.questions
												.nameOfInvestigationalDevice
										}
										className='form-control'
										disabled
									/>
								</div>
								<div className='col-sm-6'>
									<label>IDE#</label>
									<input
										type='text'
										defaultValue={study.questions.IDE}
										className='form-control'
										disabled
									/>
								</div>
							</div>
						</div>
					)}
				</form>
				<table className='table table-bordered table-hover qct-table'>
					<tbody>
						<tr>
							<th colSpan='2' className='table-header-row'>
								<div
									className='study-information-title'
									onClick={() =>
										setShowQCTA((prev) => !prev)
									}
								>
									<span className='study-information-title-label'>
										Qualifying Clinical Trial Analysis
									</span>
									{!showQCTA && (
										<Icon className='expand-control'>
											expand_more
										</Icon>
									)}
									{showQCTA && (
										<Icon className='expand-control'>
											expand_less
										</Icon>
									)}
								</div>
							</th>
						</tr>
						{showQCTA && (
							<>
								<tr>
									<td>
										Does the investigation item or service
										fall into a Medicare benefit category?
									</td>
									<td>
										<select
											disabled={editForm}
											ref={q1Ref}
											className='form-select'
										>
											<option value={q1Response}>
												{q1Response}
											</option>
											{q1Response === 'Yes' ? (
												<option value='No'>No</option>
											) : (
												<option value='Yes'>Yes</option>
											)}
										</select>
									</td>
								</tr>
								<tr>
									<td>
										Does the study have therapeutic intent
										stated in the protocol's primary or
										secondary objectives?
									</td>
									<td>
										<select
											disabled={editForm}
											ref={q2Ref}
											className='form-select'
										>
											<option value={q2Response}>
												{q2Response}
											</option>
											{q2Response === 'True' ||
											q2Response === 'True' ? (
												<option value='No'>No</option>
											) : (
												<option value='Yes'>Yes</option>
											)}
										</select>
									</td>
								</tr>
								<tr>
									<td>
										Does the study enroll subjects with a
										diagnosed disease? (not healthy
										volunteers only)
									</td>
									<td>
										<select
											disabled={editForm}
											ref={q3Ref}
											className='form-select'
										>
											<option value={q3Response}>
												{q3Response}
											</option>
											{q3Response === 'Yes' ? (
												<option value='No'>No</option>
											) : (
												<option value='Yes'>Yes</option>
											)}
										</select>
									</td>
								</tr>
								<tr>
									<td>
										Is this study automatically deemed?
									</td>
									<td>
										<select
											disabled={editForm}
											ref={q4Ref}
											className='form-select'
										>
											<option value={q4Response}>
												{q4Response}
											</option>
											{q4Response === 'Yes' ? (
												<option value='No'>No</option>
											) : (
												<option value='Yes'>Yes</option>
											)}
										</select>
									</td>
								</tr>
								<tr>
									<td>
										From the answers above, is this a
										qualifying trial?
									</td>
									<td>
										<select
											disabled={editForm}
											ref={q5Ref}
											className='form-select'
										>
											<option value={q5Response}>
												{q5Response}
											</option>
											{q5Response === 'Yes' ? (
												<option value='No'>No</option>
											) : (
												<option value='Yes'>Yes</option>
											)}
										</select>
									</td>
								</tr>
							</>
						)}
					</tbody>
				</table>
				{authCtx.userData.role === 'QA' &&
					showButtons &&
					!study.approved && (
						<div className='qct-form-buttons'>
							{editForm === true ? (
								<Button
									className='seconday-button'
									onClick={onEditForm}
								>
									Edit Information
								</Button>
							) : (
								<Button
									className='seconday-button'
									onClick={onCancelEditForm}
								>
									Cancel
								</Button>
							)}

							<Button
								className='primary-button'
								onClick={onSendStudyInfo}
							>
								Approve
							</Button>
						</div>
					)}
			</div>
		</>
	);
};

export default StudyQCTForm;
